import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { selectState } from "../../../store/slices/themeSlice";
import { selectAnalyticsState } from "../../../store/slices/analyticsSlice";
import {
  fetchTelemetryGroupedByField,
  fetchTelemetryPercentages,
} from "../../../store/actions/analyticsActions";
import { AppDispatch } from "../../../store/store";
import useCustomStyles from "../../../hooks/useCustomStyles";
import { clientsAnalyticsStyles } from "../../../assets/styles/ClientsStyles/clientsAnalyticsStyles";
import { clientsAnalyticsOverviewStyles } from "../../../assets/styles/ClientsStyles/clientsAnalyticsOverviewStyles";
import { ReactComponent as ArrowRight } from "../../../assets/images/icon-arrow-right.svg";
import { ReactComponent as ArrowDown } from "../../../assets/images/icon-arrow-down.svg";
import { ReactComponent as ArrowLeft } from "../../../assets/images/icon-arrow-left.svg";
import {
  TelemetryGroupByField,
  TELEMETRY_GROUP_BY_FIELDS,
} from "../../../api/awsApis";
import { ClientType } from "../../../store/slices/clientsSlice";
import MetricDonutCard from "./MetricDonutCard";

const FIELD_LABELS: Record<TelemetryGroupByField, string> = {
  deviceOs: "Device OS",
  version: "App Version",
  notificationsEnabled: "Notifications Enabled",
  grantedLocationPermission: "Granted Location Permission",
  deviceTimeZone: "Device Time Zone",
  activeFbar: "Active FBAR",
  activeGeoMonitor: "Active Geo Monitor",
};

interface ClientsAnalyticsProps {
  clients: ClientType[];
  onClientClick?: (client: ClientType) => void;
}

const ClientsAnalytics: React.FC<ClientsAnalyticsProps> = ({
  clients,
  onClientClick,
}) => {
  const { theme } = useSelector(selectState);
  const classes = useCustomStyles(clientsAnalyticsStyles, theme);
  const overviewClasses = useCustomStyles(clientsAnalyticsOverviewStyles, theme);
  const dispatch = useDispatch<AppDispatch>();

  const {
    telemetryField: selectedField,
    telemetryResult: result,
    loading,
    percentages,
    percentagesLoading,
    percentagesError,
  } = useSelector(selectAnalyticsState);

  const [view, setView] = useState<"overview" | "detail">("overview");
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});

  const clientByUserId = useMemo(() => {
    const map: Record<string, ClientType> = {};
    (clients || []).forEach((client) => {
      if (client.app_user_id) map[client.app_user_id] = client;
    });
    return map;
  }, [clients]);

  // Overview charts are the default view: load percentages once, reuse afterwards
  useEffect(() => {
    if (!percentages && !percentagesLoading) {
      dispatch(fetchTelemetryPercentages())
        .unwrap()
        .catch((error) => {
          console.error("Error fetching telemetry percentages:", error);
          toast.error("Error fetching analytics overview");
        });
    }
  }, [percentages, percentagesLoading, dispatch]);

  // Collapse every group by default whenever new data is loaded/displayed
  useEffect(() => {
    if (!result) return;
    const initialCollapsed: Record<string, boolean> = {};
    Object.keys(result.groups).forEach((key) => {
      initialCollapsed[key] = true;
    });
    setCollapsedGroups(initialCollapsed);
  }, [result]);

  const selectField = async (field: TelemetryGroupByField) => {
    setView("detail");
    // Only re-fetch when a different field than the currently persisted one is picked
    if (field === selectedField && result) return;
    try {
      await dispatch(fetchTelemetryGroupedByField(field)).unwrap();
    } catch (error) {
      console.error("Error fetching telemetry groups:", error);
      toast.error("Error fetching analytics data");
    }
  };

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const sortedGroupEntries = useMemo(() => {
    if (!result) return [];
    return Object.entries(result.groups)
      // Only keep userIds that match a known client; drop groups left with none
      .map(
        ([groupValue, userIds]) =>
          [
            groupValue,
            userIds.filter((userId) => Boolean(clientByUserId[userId])),
          ] as [string, string[]],
      )
      .filter(([, userIds]) => userIds.length > 0)
      .sort(([a], [b]) => {
        if (a === "unknown") return 1;
        if (b === "unknown") return -1;
        return a.localeCompare(b);
      });
  }, [result, clientByUserId]);

  const totalMatchedUsers = useMemo(
    () =>
      sortedGroupEntries.reduce((sum, [, userIds]) => sum + userIds.length, 0),
    [sortedGroupEntries],
  );

  return (
    <div className={classes.container}>
      {view === "detail" && (
        <div
          className={overviewClasses.backLink}
          onClick={() => setView("overview")}
        >
          <ArrowLeft className={classes.icon} />
          Back to overview
        </div>
      )}

      {view === "overview" ? (
        <>
          {percentagesLoading && (
            <p className={classes.emptyText}>Loading...</p>
          )}

          {!percentagesLoading && !percentages?.metrics && (
            <p className={classes.emptyText}>
              {percentagesError
                ? `Error loading analytics: ${percentagesError}`
                : "No analytics data available yet."}
            </p>
          )}

          {!percentagesLoading && percentages?.metrics && (
            <>
              <p className={overviewClasses.summaryText}>
                Based on {percentages.totalUsers} user
                {percentages.totalUsers === 1 ? "" : "s"}. Click a chart to
                see the matching clients.
              </p>
              <div className={overviewClasses.grid}>
                {TELEMETRY_GROUP_BY_FIELDS.map((field) => (
                  <MetricDonutCard
                    key={field}
                    label={FIELD_LABELS[field]}
                    data={percentages.metrics[field] || {}}
                    onClick={() => selectField(field)}
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          {loading && (
            <div className={classes.loadingWrap}>
              <div className={classes.loadingIcon} role="status" aria-label="Loading" />
            </div>
          )}

          {!loading && !result && (
            <p className={classes.emptyText}>
              {selectedField
                ? `No data available for ${FIELD_LABELS[selectedField]}.`
                : "No data available."}
            </p>
          )}

          {!loading && result && sortedGroupEntries.length === 0 && (
            <p className={classes.emptyText}>
              No clients found for {FIELD_LABELS[result.field]}.
            </p>
          )}

          {!loading && result && sortedGroupEntries.length > 0 && (
            <>
              <p className={classes.summaryText}>
                {totalMatchedUsers} user{totalMatchedUsers === 1 ? "" : "s"}{" "}
                grouped by {FIELD_LABELS[result.field]}
              </p>

              <div className={classes.groupsContainer}>
                {sortedGroupEntries.map(([groupValue, userIds]) => {
                  const isCollapsed = collapsedGroups[groupValue] !== false;
                  return (
                    <div key={groupValue}>
                      <div
                        className={classes.groupHeader}
                        onClick={() => toggleGroup(groupValue)}
                      >
                        <div className={classes.groupHeaderLeft}>
                          {isCollapsed ? (
                            <ArrowRight className={classes.icon} />
                          ) : (
                            <ArrowDown className={classes.icon} />
                          )}
                          <span className={classes.groupLabel}>
                            {groupValue}
                          </span>
                        </div>
                        <span className={classes.groupCount}>
                          {userIds.length} user
                          {userIds.length === 1 ? "" : "s"}
                        </span>
                      </div>

                      {!isCollapsed && (
                        <div className={classes.groupBody}>
                          <table className={classes.table}>
                            <thead>
                              <tr>
                                <th className={classes.th}>Email</th>
                                <th className={classes.th}>Business</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userIds.map((userId) => {
                                const client = clientByUserId[userId];
                                return (
                                  <tr
                                    key={userId}
                                    className={classes.tr}
                                    onClick={() => onClientClick?.(client)}
                                  >
                                    <td className={classes.td}>
                                      {client.email}
                                    </td>
                                    <td className={classes.td}>
                                      {client.business_name || "-"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ClientsAnalytics;
